import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { message } from 'antd';
import DeleteIcon from 'assets/deleteIcon.svg?react';
import PDFIcon from 'assets/pdf.svg?react';
import DuplicateIcon from 'assets/file_copy_icon.svg?react';
import AddIcon from 'assets/add.svg?react';
import { useStore } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import showConfirmModal from 'components/ConfirmModal';
import SpinnerModal from 'components/SpinnerModal';
import { paths } from 'routes/paths';
import useInvoiceActions from 'screens/Invoices/hooks/useInvoiceActions';
import EditButton from 'screens/Applications&WorkOrders/Components/ActionMenu/components/EditButton';
import DropdownMenu, {
  MenuItem
} from 'screens/Applications&WorkOrders/Components/ActionMenu/components/DropdownMenu';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import invoiceApi from '../helpers/invoiceApi';
import invoiceNotification from './Notification';
import NOTIFICATION_STRINGS from './Notification/Constants/notificationString';
import useInvoiceEvents from '../hooks/useInvoiceEvents';
import { InvoiceFlag } from '../types';
import './InvoiceActionMenu.css';

/**
 * Typed view of the subset of the (untyped `.jsx`) `useInvoiceActions` return
 * the action menu consumes.
 */
interface InvoiceActionsApi {
  handleAction: (action: string, invoice: InvoiceActionMenuInvoice) => void;
  downloadingPDF: boolean;
}

// The shared DropdownMenu/ActionMenu CSS forces every menu icon's `fill` to
// `currentColor !important` so it can track each item's own text color
// (e.g. red for a destructive action in Applications). Invoices wants every
// icon in neutral-60 regardless of the item's text color (FR-087), so pin
// `color` on the icon itself — nearer than the button's `color`, so
// `currentColor` resolves to this instead.
const ICON_STYLE = {
  width: '18px',
  height: '18px',
  color: '#696F88'
} as const;
const DEFAULT_TEXT_COLOR = '#1F2937';
const DEFAULT_HOVER_BG = '#F3F4F6';
const DELETE_TEXT_COLOR = '#CF3537';
const DELETE_HOVER_BG = '#FFF1F1';
// The destructive Delete action renders its icon in the same red as its label
// (FR-097 parity with the Applications action menu), rather than the neutral
// `ICON_STYLE` every other item uses. Pin `color` here so it wins over the
// shared DropdownMenu CSS's `fill: currentColor !important`.
const DELETE_ICON_STYLE = {
  width: '18px',
  height: '18px',
  color: DELETE_TEXT_COLOR
} as const;

/**
 * Shape `useInvoiceActions.handleAction` expects for an invoice. The detail
 * panel builds this from the fetched details + the selected summary row (id →
 * `invoiceId`, `invoiceNumber` → `title`, `flagIds` → `flags`).
 */
export interface InvoiceActionMenuInvoice {
  invoiceId: string;
  title: string;
  invoiceNumber?: string;
  vendor?: string;
  flags?: InvoiceFlag[];
  invoiceDate?: string | null;
  invoiceDateEpoch?: number | null;
  invoiceDueDate?: string | null;
  invoiceDueDateEpoch?: number | null;
  cropSeasons?: Array<{ name: string }>;
  invoiceProducts?: unknown[];
  flagIds?: string[];
  notes?: string;
}

export interface InvoiceActionMenuProps {
  invoice: InvoiceActionMenuInvoice;
  /**
   * Whether the member can perform write actions (Full control / Admin). When
   * `false` (View Only) every write/destructive action is hidden, leaving a
   * read-only panel — matching the legacy Full-control-gated invoice menu.
   */
  canModify?: boolean;
  /** Fired after a successful delete so the list can re-select + refetch. */
  onInvoiceDeleted?: (invoiceId: string) => void;
}

/**
 * Action menu for the invoice detail panel. Mirrors the Applications/Work Orders
 * `ActionMenu` layout (primary Edit button + overflow `DropdownMenu`). Most
 * actions route through the existing `useInvoiceActions.handleAction` so
 * behavior matches the legacy landing menu; Delete bypasses it and calls the
 * invoice API directly behind the Applications-style `showConfirmModal`
 * confirmation. Destructive/write actions are gated by `canModify`.
 * "Create Task" is never shown for invoices (FR-054 supersedes the
 * Tasks-flag conditional).
 */
const InvoiceActionMenu: React.FC<InvoiceActionMenuProps> = ({
  invoice,
  canModify = false,
  onInvoiceDeleted
}) => {
  const history = useHistory();
  const { dispatch } = useStore();
  const {
    handleAction,
    downloadingPDF
  } = (useInvoiceActions() as unknown) as InvoiceActionsApi;
  const {
    sendInitiateEditEvent,
    sendInitiateDuplicateEvent,
    sendDownloadPdfEvent,
    sendDeleteEvent
  } = useInvoiceEvents();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = (id: string) => {
    const deleteStartedAt = performance.now();
    setIsDeleting(true);
    const { promise } = invoiceApi.delete(id);
    promise
      .then(() => {
        sendDeleteEvent(
          'Success',
          Math.round(performance.now() - deleteStartedAt)
        );
        invoiceNotification('DELETE_SUCCESS');
        onInvoiceDeleted?.(id);
      })
      .catch(catchCancel)
      .catch((err: unknown) => {
        sendDeleteEvent(
          'Error',
          Math.round(performance.now() - deleteStartedAt),
          err
        );
        invoiceNotification('DELETE_ERROR');
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsDeleting(false));
  };

  const confirmDelete = () => {
    // Every other menu action routes through `handleAction`, which always
    // clears the record-source banner first — Delete bypasses that hook, so
    // it must clear the banner itself to match.
    message.destroy(RECORD_SOURCE_MESSAGE_KEY);
    showConfirmModal({
      title: 'Delete Invoice?',
      content: (
        <>
          You will permanently lose{' '}
          <span className="font-semibold">all data</span> from this record.
        </>
      ),
      contentStyles: { width: 375, fontSize: 14, lineHeight: '20px' },
      okText: 'Delete record',
      cancelText: 'Go back',
      onOk: () => handleDelete(invoice.invoiceId)
    });
  };

  const menuItems: MenuItem[] = [];

  if (canModify) {
    menuItems.push(
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: <DuplicateIcon style={ICON_STYLE} />,
        textColor: DEFAULT_TEXT_COLOR,
        hoverBgColor: DEFAULT_HOVER_BG,
        onClick: () => {
          sendInitiateDuplicateEvent('Detail View menu');
          history.push(`${paths.invoices}/${invoice.invoiceId}/duplicate`);
        }
      },
      {
        key: 'download-pdf',
        label: 'Download PDF',
        icon: <PDFIcon style={ICON_STYLE} />,
        textColor: DEFAULT_TEXT_COLOR,
        hoverBgColor: DEFAULT_HOVER_BG,
        onClick: () => {
          sendDownloadPdfEvent('Detail View menu');
          handleAction('downloadPDFEnglish', invoice);
        }
      },
      {
        key: 'create-work-order',
        label: 'Create Work Order',
        icon: <AddIcon style={ICON_STYLE} />,
        textColor: DEFAULT_TEXT_COLOR,
        hoverBgColor: DEFAULT_HOVER_BG,
        onClick: () => {
          handleAction('createWorkOrder', invoice);
        }
      },
      {
        key: 'create-application',
        label: 'Create Application',
        icon: <AddIcon style={ICON_STYLE} />,
        textColor: DEFAULT_TEXT_COLOR,
        hoverBgColor: DEFAULT_HOVER_BG,
        onClick: () => {
          handleAction('createApplication', invoice);
        }
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteIcon style={DELETE_ICON_STYLE} />,
        textColor: DELETE_TEXT_COLOR,
        hoverBgColor: DELETE_HOVER_BG,
        onClick: confirmDelete
      }
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="invoice-action-menu">
      {canModify && (
        <EditButton
          onEdit={() => {
            sendInitiateEditEvent();
            history.push(`${paths.invoices}/${invoice.invoiceId}/edit`);
          }}
        />
      )}
      {menuItems.length > 0 && <DropdownMenu items={menuItems} />}

      <SpinnerModal open={downloadingPDF} message="Downloading PDF" />
      <SpinnerModal
        open={isDeleting}
        message={NOTIFICATION_STRINGS.DELETING_INVOICE_MESSAGE}
      />
    </div>
  );
};

export default InvoiceActionMenu;
